import { Space } from "antd";
import Link from "../../../components/link";
import { formatAuthor } from "../../../utils/formatFields";
import { Heading, Paragraph } from "../../../components/typography";
import PropertyList from "../../../components/property-list";

export const listFormatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

export const renderAuthors = (authors, prefix = "", suffix = "", includeInstitutions = true) => {
  if (!authors || authors.length === 0) return null;
  return (
    prefix +
    listFormatter.format(
      authors.map((author) => formatAuthor(author, includeInstitutions, includeInstitutions)),
    ) +
    suffix
  );
};

export const renderEditors = (editors) => {
  if (!editors || editors.length === 0) return null;
  return listFormatter.format(
    editors.map((editor) => formatAuthor(editor, true, true)),
  );
};

export const renderPublishDate = (publishDate) => {
  if (!publishDate) return null;
  let format = "year";
  let date_str;
  // Not sure if the format will be string or object in shape { dateFormat/format: 'year' | 'month' | 'day', date: 'YYYY(-MM)(-DD)' }
  if (typeof publishDate === "string") {
    const hyphenCount = (publishDate.match(/-/g) || []).length;
    if (hyphenCount) format = hyphenCount === 1 ? "month" : "day";
    date_str = publishDate;
  } else {
    format = publishDate.dateFormat ?? publishDate.format ?? format;
    date_str = publishDate.date;
  }
  const dateOptions = { year: "numeric", timeZone: "UTC" };
  if (format === "month" || format === "day") dateOptions["month"] = "long";
  if (format === "day") dateOptions["day"] = "numeric";
  return new Date(date_str).toLocaleDateString("en-CA", dateOptions);
};

export const renderConcept = (concept, prefix = "", suffix = "") => {
  if (!concept || !concept.concepts.length) return null;
  return (
    prefix + listFormatter.format(concept.concepts.map((c) => c.name)) + suffix
  );
};

// export const renderEducationalLevels = (educationalLevels) => {
//     if (!educationalLevels || educationalLevels.concepts.length === 0) return null;
//     return educationalLevels.concepts.map(level => level.name).join(", ");
// };

export const renderLicense = (license) => {
  if (!license || license.concepts.length === 0) return null;
  const concept = license.concepts[0];
  return (
    <>
      <Link rel="license" href={concept.meta.referenceURL}>
        {concept.name}
      </Link>{" "}
      except where otherwise noted
    </>
  );
};

export const getDLSubjects = (subjects) =>
  subjects?.concepts.filter(
    (s) =>
      s.code === "digital-literacy" ||
      s.broader?.some((sp) => sp.code === "digital-literacy"),
  );

export const renderDLCompetencies = (dlSubjects) => {
  if (!dlSubjects || dlSubjects.length === 0) return null;
  return (
    <Space className="bccampus-features" wrap>
      {dlSubjects.map((subject) =>
        subject.children?.length > 0 ? (
          subject.children.map((child) => (
            <span key={child.id} className="bccampus-feature-tag style-2">
              {child.name}
            </span>
          ))
        ) : (
          <span key={subject.id} className="bccampus-feature-tag style-2">
            {subject.name}
          </span>
        ),
      )}
    </Space>
  );
};

export const RenderDLSection = ({ resource }) => {
  const dlSubjects = getDLSubjects(
    resource.concepts.subject ?? { concepts: [] },
  );

  if (dlSubjects.length) {
    return (
      <div style={{ marginTop: "2em" }}>
        <Heading level={3}>Digital Literacy</Heading>
        <Paragraph>
          This resource supports the development of skills and competencies in
          the B.C. Post-Secondary Digital Literacy Framework and aligns with the
          following competencies.
        </Paragraph>
        <PropertyList
          items={[
            // { name: "Digital Audience", value: <span>For audiences with <strong>beginner or intermediate</strong> digital literacy levels</span> },
            {
              name: "Digital Competencies",
              value: renderDLCompetencies(dlSubjects),
            },
          ]}
        />
      </div>
    );
  }
};

export const RenderResourceAncestors = ( resource ) => {
  const topLevelAncestors = resource.resourceAncestors?.topLevelParents;
  if(topLevelAncestors) {
    const response = [];
    for(let i = 0; i < topLevelAncestors.length; i ++ ) {
      response.push(
        <Link 
            key={topLevelAncestors[i].code}
            href={`/resource/${topLevelAncestors[i].code}`}
            title={`View ${topLevelAncestors[i].name}`}
          >
            {topLevelAncestors[i].name}
          </Link>
      );
      const oxfordComma = topLevelAncestors.length > 2 ? "," : "";
      if( i === (topLevelAncestors.length - 2)) response.push(`${oxfordComma} and `);
      else if( i < (topLevelAncestors.length - 1)) response.push(", ");
    }
    return response;
  }
}

export const RenderAttribution = ( resource ) => {
  let resourceName = resource.name || "Untitled Work";
  if( resource.meta.subtitle && resource.meta.subtitle.length > 0) {
    resourceName += `: ${resource.meta.subtitle}`;
  }
  if( resource.meta.edition && resource.meta.edition.length > 0) {
    // Arianna specifically requested an en-dash here
    resourceName += ` – ${resource.meta.edition}`;
  }

  const authors = renderAuthors(resource.contributors['relator/aut'], " by ", "", false);
  const license_concept = resource.concepts.license?.concepts[0];
  const license_link = license_concept ? ( license_concept.meta.referenceURL ? <a href={license_concept.meta.referenceURL} title="Read about what this license means">{license_concept.name}</a> : license_concept.name) : "unknown";
  const license = <span> is licensed under a {license_link} license</span>;

  return (
    <span>
      <a href={`/resource/${resource.code}`} style={{fontStyle: "italic"}} title="View this resource on the B.C. Open Collection">{resourceName}</a>
      {authors && <>{authors}</>}
      <>{license}</>
      .
    </span>
  );
}
